import { Plus, Trash2, Warehouse, X } from 'lucide-react';
import { Badge, Button, formatNumber, IconButton, Input, Select } from '@platform/design-system';
import { useTranslation } from '@platform/i18n';
import { useId } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import type { Location, StockLevel } from '../../inventory/inventory.ts';
import { combinationKey, variantLabel, type OptionIndex } from '../options.ts';
import type { ProductFormValues } from '../product-form.ts';
import { depotUnits, type StockAcceptance } from './stock-columns.ts';
import { useFieldError } from './sections.ts';
import styles from './StockSection.module.css';

export type DepotStockCardProps = {
  location: Location;
  levels: readonly StockLevel[];
  index: OptionIndex;
  accepts: StockAcceptance;
  onDrop: (() => void) | null;
};

type VariantChoice = { readonly value: string; readonly label: string };

export const DepotStockCard = ({ location, levels, index, accepts, onDrop }: DepotStockCardProps) => {
  const { t, i18n } = useTranslation();
  const { control, register, formState } = useFormContext<ProductFormValues>();
  const errorText = useFieldError();
  const headingId = useId();
  const splitsPath = `stockSplits.${location.id}` as const;
  const splits = useFieldArray({ control, name: splitsPath });
  const [rows, typed] = useWatch<ProductFormValues, ['variants', `stockTotals.${string}`]>({
    control,
    name: ['variants', `stockTotals.${location.id}`],
  });
  const chosen = useWatch({ control, name: splitsPath, defaultValue: [] });
  const choices: VariantChoice[] = rows
    .filter((row) => row.enabled && accepts(row.variantId))
    .map((row) => ({
      value: combinationKey(row.valueIds),
      label: variantLabel(row.valueIds, index, t('productEditor.singleVariant')),
    }));
  const totalLabel = t('productEditor.stockTotal');
  const recorded = depotUnits({ rows, locationId: location.id, levels, accepts, typed });
  const taken = new Set(chosen.map((split) => split.combination));
  const nextChoice = choices.find((choice) => !taken.has(choice.value));
  const depotSplitErrors = formState.errors.stockSplits?.[location.id];
  const mismatch = errorText(depotSplitErrors?.root ?? depotSplitErrors);
  const splitHelp =
    choices.length > 1 && splits.fields.length === 0
      ? t('productEditor.stockSplitHelp', { count: choices.length })
      : undefined;
  const choicesFor = (position: number) =>
    choices.filter((choice) => choice.value === chosen[position]?.combination || !taken.has(choice.value));

  return (
    <section className={styles['depot']} aria-labelledby={headingId}>
      <header className={styles['depotHead']}>
        <span className={styles['depotIcon']} aria-hidden="true">
          <Warehouse size={20} />
        </span>
        <h3 id={headingId} className={styles['depotName']}>
          {location.name}
        </h3>
        {location.isDefault ? <Badge tone="info">{t('productEditor.stockDefault')}</Badge> : null}
        {onDrop === null ? null : (
          <span className={styles['depotRemove']}>
            <IconButton
              size="sm"
              label={t('productEditor.stockDropDepot', { depot: location.name })}
              icon={<X size={16} aria-hidden="true" />}
              onClick={onDrop}
            />
          </span>
        )}
      </header>
      {choices.length === 0 ? (
        <div className={styles['legacyRow']}>
          <Input
            label={totalLabel}
            aria-label={`${totalLabel} — ${location.name}`}
            dir="ltr"
            readOnly
            value={formatNumber(recorded, i18n.language)}
          />
        </div>
      ) : (
        <>
          <div className={styles['legacyRow']}>
            <Input
              label={totalLabel}
              aria-label={`${totalLabel} — ${location.name}`}
              helperText={splitHelp}
              inputMode="numeric"
              dir="ltr"
              placeholder="0"
              errorText={errorText(formState.errors.stockTotals?.[location.id])}
              {...register(`stockTotals.${location.id}`)}
            />
            <Input
              label={t('productEditor.stockThreshold')}
              inputMode="numeric"
              dir="ltr"
              placeholder={t('productEditor.stockThresholdPlaceholder')}
              errorText={errorText(formState.errors.thresholds?.[location.id])}
              {...register(`thresholds.${location.id}`)}
            />
          </div>
          {choices.length > 1 ? (
            <div className={styles['splits']}>
              {splits.fields.map((field, position) => (
                <div key={field.id} className={styles['splitRow']}>
                  <Select
                    label={t('productEditor.stockSplitVariant')}
                    options={choicesFor(position)}
                    {...register(
                      `stockSplits.${location.id}.${String(position)}.combination` as `stockSplits.${string}.${number}.combination`,
                    )}
                  />
                  <Input
                    label={t('productEditor.stockSplitQuantity')}
                    inputMode="numeric"
                    dir="ltr"
                    placeholder="0"
                    errorText={errorText(depotSplitErrors?.[position]?.quantity)}
                    {...register(
                      `stockSplits.${location.id}.${String(position)}.quantity` as `stockSplits.${string}.${number}.quantity`,
                    )}
                  />
                  <IconButton
                    label={t('productEditor.stockSplitRemove', { position: position + 1 })}
                    icon={<Trash2 size={18} aria-hidden="true" />}
                    onClick={() => {
                      splits.remove(position);
                    }}
                  />
                </div>
              ))}
              {mismatch === undefined ? null : (
                <p role="alert" className={styles['splitError']}>
                  {mismatch}
                </p>
              )}
              <Button
                variant="outline"
                size="sm"
                icon={<Plus size={16} aria-hidden="true" />}
                disabled={nextChoice === undefined}
                className={styles['addSplit']}
                onClick={() => {
                  if (nextChoice !== undefined)
                    splits.append({ combination: nextChoice.value, quantity: '' });
                }}
              >
                {t('productEditor.stockAddVariant')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};
